import type { ComponentProps, ReactNode } from "react";
import styled from "@emotion/styled";

import { MDSBadge, MDSBadgeVariant } from "@/mds/components/badge";
import { MDSButton, MDSButtonVariant } from "@/mds/components/buttons/button";
import { MDSIcon, MDSIconColor, MDSIconKind } from "@/mds/components/icon/icon";
import { MDSModalDialog } from "@/mds/components/overlays/dialog";

export type PaywallPlanTone = "default" | "emphasis";
export type PaywallPlanFeatureLayout = "single-line" | "paragraph";
export type PaywallPlanDensity = "default" | "compact";

export interface PaywallPlanCardDefinition {
  id: string;
  title: string;
  badgeLabel?: string;
  price: string;
  priceSubtitle?: string;
  features: string[];
  tone: PaywallPlanTone;
  checkboxes?: PaywallPlanTone;
  featureLayout?: PaywallPlanFeatureLayout;
  footerNote?: string;
  density?: PaywallPlanDensity;
}

interface PaywallModalDialogProps extends ComponentProps<typeof MDSModalDialog> {
  dialogWidth: string;
}

interface PaywallPlansGridProps {
  children: ReactNode;
  columnCount: number;
  collapseBelow?: string;
  hideFirstColumnBelow?: string;
  stretchCards?: boolean;
}

interface PaywallPlanCardProps {
  planCard: PaywallPlanCardDefinition;
  action?: ReactNode;
}

interface PaywallActionButtonProps extends Omit<ComponentProps<typeof MDSButton>, "variant"> {
  tone?: PaywallPlanTone;
}

export interface PaywallPlanActionProps {
  buttonLabel: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  note?: string;
  onClick: () => void;
  tone?: PaywallPlanTone;
}

const FEATURE_ICON_BOX_SIZE_PX = 16;
const FEATURE_CHECK_ICON_SIZE_PX = 11;
const SINGLE_LINE_FEATURE_LINE_HEIGHT_PX = 16;
const PARAGRAPH_FEATURE_LINE_HEIGHT_PX = 20;
const PARAGRAPH_FEATURE_ICON_TOP_OFFSET_PX = 3;

export const getPaywallDialogWidth = (planCardCount: number): string => {
  if (planCardCount === 1) {
    return "600px";
  }

  if (planCardCount === 2) {
    return "792px";
  }

  return "1024px";
};

export const PaywallModalDialog = ({
  dialogWidth,
  children,
  ...modalDialogProps
}: PaywallModalDialogProps) => (
  <StyledPaywallModalDialog
    {...modalDialogProps}
    fluidWidth
    $dialogWidth={dialogWidth}
    data-paywall-modal
  >
    {children}
  </StyledPaywallModalDialog>
);

export const PaywallPlansGrid = ({
  children,
  columnCount,
  collapseBelow = "760px",
  hideFirstColumnBelow,
  stretchCards = false,
}: PaywallPlansGridProps) => (
  <StyledPaywallPlansGrid
    $collapseBelow={collapseBelow}
    $columnCount={columnCount}
    $hideFirstColumnBelow={hideFirstColumnBelow}
    $stretchCards={stretchCards}
  >
    {children}
  </StyledPaywallPlansGrid>
);

export const PaywallPlanCard = ({ planCard, action }: PaywallPlanCardProps) => {
  const featureLayout = planCard.featureLayout ?? "single-line";
  const checkboxTone = planCard.checkboxes ?? planCard.tone;
  const density = planCard.density ?? "default";

  return (
    <StyledPlanCard $tone={planCard.tone}>
      <PlanHeader>
        <PlanTitle $density={density}>{planCard.title}</PlanTitle>
        {planCard.badgeLabel ? (
          <PlanBadge
            label={planCard.badgeLabel}
            variant={MDSBadgeVariant.Default}
            $tone={planCard.tone}
          />
        ) : null}
      </PlanHeader>

      <PlanDivider />

      <PlanBody>
        <PlanPrice $density={density} $hasSubtitle={Boolean(planCard.priceSubtitle)}>
          {planCard.price}
        </PlanPrice>
        {planCard.priceSubtitle ? (
          <PlanPriceSubtitle $density={density}>{planCard.priceSubtitle}</PlanPriceSubtitle>
        ) : null}

        <FeatureList>
          {planCard.features.map(feature => (
            <FeatureItem key={feature} $featureLayout={featureLayout}>
              <FeatureIconWrapper $featureLayout={featureLayout}>
                <MDSIcon
                  kind={MDSIconKind.Check}
                  boxSize={FEATURE_ICON_BOX_SIZE_PX}
                  size={FEATURE_CHECK_ICON_SIZE_PX}
                  color={
                    checkboxTone === "emphasis" ? MDSIconColor.Selected : MDSIconColor.Secondary
                  }
                />
              </FeatureIconWrapper>
              <FeatureText $featureLayout={featureLayout}>{feature}</FeatureText>
            </FeatureItem>
          ))}
        </FeatureList>

        {planCard.footerNote ? <FooterNote>{planCard.footerNote}</FooterNote> : null}
        {action}
      </PlanBody>
    </StyledPlanCard>
  );
};

export const PaywallActionButton = ({
  children,
  tone = "emphasis",
  ...buttonProps
}: PaywallActionButtonProps) => (
  <StyledActionButton
    variant={tone === "emphasis" ? MDSButtonVariant.Emphasis : MDSButtonVariant.Default}
    {...buttonProps}
  >
    {tone === "emphasis" ? (
      <MDSIcon kind={MDSIconKind.StarSolid} boxSize={16} size={16} color={MDSIconColor.OnAccent} />
    ) : null}
    {children}
  </StyledActionButton>
);

export const PaywallPlanAction = ({
  buttonLabel,
  isDisabled = false,
  isLoading = false,
  note,
  onClick,
  tone = "emphasis",
}: PaywallPlanActionProps) => (
  <PaywallButtonRow>
    <PlanActionButton onClick={onClick} isDisabled={isDisabled} isLoading={isLoading} tone={tone}>
      {buttonLabel}
    </PlanActionButton>
    {note ? <PlanActionNote>{note}</PlanActionNote> : null}
  </PaywallButtonRow>
);

const StyledPaywallModalDialog = styled(MDSModalDialog, {
  shouldForwardProp: prop => prop !== "$dialogWidth",
})<{ $dialogWidth: string }>`
  .mds-dialog {
    box-sizing: border-box;
    width: ${({ $dialogWidth }) => `min(${$dialogWidth}, calc(100vw - 40px))`};
    max-width: calc(100vw - 40px);
    padding: ${({ theme }) => theme.spacing["7"]};
    gap: 0;
    outline: none;
  }

  .mds-dialog [slot="header"] {
    padding-block: 0;
    padding-bottom: ${({ theme }) => theme.spacing["2"]};
  }

  .mds-dialog button[aria-label="Close"] {
    border: 0;
  }

  [data-mds-name="dialog-body"] {
    overflow-x: hidden;
  }

  &[data-paywall-modal],
  &[data-paywall-modal] * {
    letter-spacing: 0;
  }

  @media (max-width: 700px) {
    .mds-dialog {
      width: calc(100vw - 32px);
      max-width: calc(100vw - 32px);
      padding: ${({ theme }) => theme.spacing["4"]};
    }
  }
`;

export const PaywallHeader = styled.div`
  display: flex;
  min-width: 0;
  width: 100%;
  padding: 0 0 ${({ theme }) => theme.spacing["2"]} ${({ theme }) => theme.spacing["3"]};
`;

export const PaywallTitle = styled.h1`
  margin: 0;
  color: ${({ theme }) => theme.colors.text.contrast};
  font-size: 28px;
  line-height: 36px;
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  letter-spacing: 0;
`;

export const PaywallContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["4"]};
  padding: ${({ theme }) => `${theme.spacing["2"]} ${theme.spacing["3"]}`};
  background: ${({ theme }) => theme.colors.surface.modal};
`;

export const PaywallSubtitle = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 15px;
  line-height: 22px;
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  letter-spacing: 0;
`;

const StyledPaywallPlansGrid = styled.div<{
  $collapseBelow: string;
  $columnCount: number;
  $hideFirstColumnBelow?: string;
  $stretchCards: boolean;
}>`
  container-name: paywall-plans;
  container-type: inline-size;
  display: grid;
  align-items: ${({ $stretchCards }) => ($stretchCards ? "stretch" : "start")};
  grid-template-columns: ${({ $columnCount }) => `repeat(${$columnCount}, minmax(0, 1fr))`};
  gap: 10px;

  ${({ $hideFirstColumnBelow }) =>
    $hideFirstColumnBelow
      ? `
        @container paywall-plans (max-width: ${$hideFirstColumnBelow}) {
          grid-template-columns: 1fr;

          > :first-of-type {
            display: none;
          }
        }
      `
      : ""}

  @media (max-width: ${({ $collapseBelow }) => $collapseBelow}) {
    grid-template-columns: 1fr;
  }
`;

const StyledPlanCard = styled.section<{ $tone: PaywallPlanTone }>`
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.borderRadius.large};
  padding: ${({ theme }) => `${theme.spacing["5"]} 0`};
  gap: ${({ theme }) => theme.spacing["2"]};
  background: ${({ theme, $tone }) =>
    $tone === "emphasis"
      ? theme.colors.background.card.emphasis
      : theme.colors.background.card.default};
`;

const PlanHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["2"]};
  padding: 0 ${({ theme }) => theme.spacing["5"]};
`;

const PlanTitle = styled.h2<{ $density: PaywallPlanDensity }>`
  margin: 0;
  color: ${({ theme }) => theme.colors.text.contrast};
  font-size: ${({ $density }) => ($density === "compact" ? "22px" : "28px")};
  line-height: ${({ $density }) => ($density === "compact" ? "30px" : "36px")};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  letter-spacing: 0;
`;

const PlanDivider = styled.div`
  height: ${({ theme }) => theme.spacing["2"]};
  border-top: 1px solid ${({ theme }) => theme.colors.border.ghost};
`;

const PlanBody = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
`;

const PlanPrice = styled.div<{ $density: PaywallPlanDensity; $hasSubtitle: boolean }>`
  padding: ${({ theme, $hasSubtitle }) =>
    $hasSubtitle
      ? `${theme.spacing["2"]} ${theme.spacing["5"]} 0`
      : `${theme.spacing["2"]} ${theme.spacing["5"]}`};
  color: ${({ theme }) => theme.colors.text.contrast};
  font-size: ${({ $density }) => ($density === "compact" ? "22px" : "28px")};
  line-height: ${({ $density }) => ($density === "compact" ? "30px" : "36px")};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  letter-spacing: 0;
`;

const PlanPriceSubtitle = styled.div<{ $density: PaywallPlanDensity }>`
  padding: ${({ theme }) => `0 ${theme.spacing["5"]} ${theme.spacing["2"]}`};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ $density }) => ($density === "compact" ? "12px" : "18px")};
  line-height: ${({ $density }) => ($density === "compact" ? "20px" : "24px")};
  font-weight: ${({ theme, $density }) =>
    $density === "compact" ? theme.fontWeights.regular : theme.fontWeights.medium};
  letter-spacing: 0;
`;

const FeatureList = styled.ul`
  display: flex;
  flex: 1;
  flex-direction: column;
  margin: 0;
  padding: 0 ${({ theme }) => theme.spacing["5"]};
  list-style: none;
`;

const FeatureItem = styled.li<{ $featureLayout: PaywallPlanFeatureLayout }>`
  display: grid;
  grid-template-columns: ${FEATURE_ICON_BOX_SIZE_PX}px minmax(0, 1fr);
  align-items: ${({ $featureLayout }) => ($featureLayout === "paragraph" ? "start" : "center")};
  box-sizing: border-box;
  column-gap: 2px;
  height: ${({ $featureLayout }) => ($featureLayout === "paragraph" ? "auto" : "32px")};
  padding: ${({ theme }) => `${theme.spacing["2"]} 0`};
`;

const FeatureIconWrapper = styled.div<{ $featureLayout: PaywallPlanFeatureLayout }>`
  display: flex;
  height: ${FEATURE_ICON_BOX_SIZE_PX}px;
  width: ${FEATURE_ICON_BOX_SIZE_PX}px;
  margin-top: ${({ $featureLayout }) =>
    $featureLayout === "paragraph" ? `${PARAGRAPH_FEATURE_ICON_TOP_OFFSET_PX}px` : 0};
`;

const FeatureText = styled.span<{ $featureLayout: PaywallPlanFeatureLayout }>`
  min-width: 0;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 14px;
  line-height: ${({ $featureLayout }) =>
    $featureLayout === "paragraph"
      ? `${PARAGRAPH_FEATURE_LINE_HEIGHT_PX}px`
      : `${SINGLE_LINE_FEATURE_LINE_HEIGHT_PX}px`};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  letter-spacing: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: ${({ $featureLayout }) => ($featureLayout === "paragraph" ? "normal" : "nowrap")};
`;

const FooterNote = styled.p`
  margin: 0;
  padding: ${({ theme }) => `${theme.spacing["2"]} ${theme.spacing["5"]}`};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0;
`;

export const PaywallButtonRow = styled.div`
  box-sizing: border-box;
  width: 100%;
  padding: ${({ theme }) => `${theme.spacing["2"]} ${theme.spacing["3"]} 0`};
`;

const PlanActionButton = styled(PaywallActionButton)`
  margin-top: ${({ theme }) => theme.spacing["2"]};
`;

const PlanActionNote = styled.p`
  margin: 0;
  min-height: 32px;
  padding: ${({ theme }) => `${theme.spacing["2"]} ${theme.spacing["2"]} 0`};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0;
  text-align: center;
`;

const StyledActionButton = styled(MDSButton)`
  width: 100%;
  height: 40px;
  border: 0;
  padding: ${({ theme }) => theme.spacing["3"]};
  font-size: 14px;
  line-height: 16px;

  .mds-icon {
    width: 16px;
    height: 16px;
    font-size: 16px;
  }
`;

const PlanBadge = styled(MDSBadge, {
  shouldForwardProp: prop => prop !== "$tone",
})<{ $tone: PaywallPlanTone }>`
  height: 16px;
  padding: 0 ${({ theme }) => theme.spacing["1"]};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  color: ${({ theme, $tone }) =>
    $tone === "emphasis" ? theme.colors.text.onAccent : theme.colors.text.secondary};
  background-color: ${({ theme, $tone }) =>
    $tone === "emphasis"
      ? theme.colors.background.button.emphasis.default
      : theme.colors.background.badge.default};
  font-size: 12px;
  line-height: 16px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
`;
